import React, { useContext, useEffect, useState } from 'react';

import { Alert, Typography, Divider, Comment, Avatar, Spin, Card, message } from 'antd';
import { StyledHeader } from '../common/Styles';
import { getTranslatedString } from 'helpers/i18n';
import { ApplicationUIContext, getInitialsForAvatar, helpers } from '@yuhuinc/ui-library';

import { format } from 'date-fns';

import PropTypes from 'prop-types';
import AddNote from './notes/AddNote';
import { createApplicationNote, getApplicationNotes } from '../store/actions';

const { COMPACT_CARD_WIDTH } = helpers;

const { Title, Paragraph } = Typography;

const sortingCreatedAtDescending = (a, b) => new Date(a.created_at) - new Date(b.created_at);

const Notes = ({ applicationId }) => {
  const { isMobile } = useContext(ApplicationUIContext);
  const [submitting, setSubmitting] = useState(false);
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errorLoadingNotes, setErrorLoadingNotes] = useState(false);

  const loadApplicationNotes = async () => {
    setLoading(true);

    getApplicationNotes(applicationId)
      .done(({ notes }) => {
        setNotes(notes);
        setLoading(false);
        setErrorLoadingNotes(false);
      })
      .catch(() => {
        setErrorLoadingNotes(true);
        setLoading(false);
      });
  };

  useEffect(() => {
    loadApplicationNotes();
  }, []);

  const onSubmitNewNote = ({ note }) => {
    setSubmitting(true);

    createApplicationNote(applicationId, { note: { text: note } })
      .done(() => {
        setSubmitting(false);
        // Load new notes
        loadApplicationNotes();
      })
      .catch(() => {
        message.error(
          getTranslatedString(
            'landlordPortal.applicationManagement.applicationOverview.notes.errorAddingNote'
          )
        );
        setSubmitting(false);
      });
  };

  const renderNotes = (notes) =>
    notes
      .sort(sortingCreatedAtDescending)
      .map(({ created_by, text, created_at }, index) => (
        <Comment
          key={index}
          author={created_by}
          content={<p>{text}</p>}
          actions={[
            <span className="ant-comment-author-time">
              {format(new Date(created_at), 'yyyy-MM-dd @ h:mm a')}
            </span>,
          ]}
          avatar={<Avatar>{getInitialsForAvatar(created_by)}</Avatar>}
        />
      ));

  return (
    <Card style={{ maxWidth: COMPACT_CARD_WIDTH }}>
      <StyledHeader>
        <Title level={isMobile ? 3 : 4}>
          {getTranslatedString('landlordPortal.applicationManagement.applicationOverview.notes')}
        </Title>
      </StyledHeader>

      <Paragraph>
        {getTranslatedString(
          'landlordPortal.applicationManagement.applicationOverview.notes.subtitle'
        )}
      </Paragraph>

      {errorLoadingNotes && (
        <Alert
          type="error"
          message={getTranslatedString(
            'landlordPortal.applicationManagement.applicationOverview.notes.errorLoadingNotes'
          )}
          action={() => setErrorLoadingNotes(false)}
          closable
        />
      )}

      <Divider />

      {renderNotes(notes)}

      {loading && <Spin />}

      <AddNote onSubmit={onSubmitNewNote} disabled={loading || submitting} />
    </Card>
  );
};

Notes.propTypes = {
  applicationId: PropTypes.string.isRequired,
};

export default Notes;
